//TODO: Properly handle user tabbing

import React, { useLayoutEffect, useCallback, useEffect, useState, useMemo, useRef } from 'react';

import {
  VStack,
  HStack,
  Flex,
  Box,
  Icon,
  Text,
  IconButton,
  IconButtonProps,
} from '@chakra-ui/react';

import { motion, useAnimation, useMotionValue } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { useBoundingRect } from '@olo-web/utils/common/hooks/useBoundingRect';
import { MotionBox } from '@olo-web/components/atoms/motion/MotionBox.ui';
import { useScreenSizeState } from '@olo-web/client-state';
import { useMount } from 'react-use';
import { useSendEvent } from '@olo-web/utils/common/hooks';
import { EAnalyticsEventNames, EGALocations } from '@olo-web/types/enums';
import { useGlobalUIState, useGlobalUIDispatch } from '@olo-web/client-state';

const MotionFlex = motion(Flex);

const transitionProps = {
  stiffness: 400,
  type: 'spring',
  damping: 60,
  mass: 3,
};

const titleVariants = {
  visible: {
    opacity: 1,
  },
  hidden: {
    opacity: 0,
  },
};

const itemVariants = {
  visible: {
    opacity: 1,
    x: '0px',
  },
  hidden: {
    opacity: 0,
    x: '500px',
  },
};

const iconButtonStyles: Omit<IconButtonProps, 'aria-label'> = {
  variant: 'solid',
  bg: 'blackAlpha.50',
  color: 'blackAlpha.900',
  _hover: {
    bg: 'blackAlpha.200',
  },
  borderRadius: 'full',
  size: 's',
  w: 6,
  h: 6,
};

const TitleAndIcons = ({
  setTrackIsActive,
  initSliderWidth,
  setActiveItem,
  children,
  gap,
  atStart,
  atEnd,
  allItemsFit,
  isLoading,
}) => {
  const [ref, { width }] = useBoundingRect();

  useLayoutEffect(() => initSliderWidth(Math.round(width)), [width, initSliderWidth]);
  const handleFocus = () => setTrackIsActive(true);

  const handleDecrementClick = () => {
    setTrackIsActive(true);
    !atStart && setActiveItem((prev) => prev - 1);
  };

  const handleIncrementClick = () => {
    setTrackIsActive(true);
    !atEnd && setActiveItem((prev) => prev + 1);
  };

  return (
    <>
      <MotionBox
        variants={titleVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.3, duration: 0.3 }}
        display="flex"
        w="100%"
        alignItems="center"
        pr={{ base: 1 }}
        mt={{ base: 0, md: 6 }}
        justifyContent="space-between"
      >
        <Text textStyle="p" fontSize="sm" fontWeight="semibold">
          Craving something extra?
        </Text>
        {!allItemsFit && (
          <HStack mx={0} pr={2}>
            <IconButton
              {...iconButtonStyles}
              onClick={handleDecrementClick}
              onFocus={handleFocus}
              isDisabled={atStart || isLoading}
              aria-label="View previous recommended item"
              icon={<Icon as={ChevronLeft} />}
            />
            <IconButton
              {...iconButtonStyles}
              onClick={handleIncrementClick}
              onFocus={handleFocus}
              isDisabled={atEnd || isLoading}
              aria-label="View next recommended item"
              icon={<Icon as={ChevronRight} />}
            />
          </HStack>
        )}
      </MotionBox>
      <Box
        ref={ref}
        w={{ base: '100%', md: `calc(100% + ${gap}px)` }}
        ml={{ base: 0 }}
        position="relative"
        overflow="hidden"
      >
        <MotionBox
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ type: 'spring', delay: 0.45, duration: 0.6, mass: 0.5, damping: 11 }}
          w="100%"
        >
          {children}
        </MotionBox>
      </Box>
    </>
  );
};

const Track = ({
  setTrackIsActive,
  trackIsActive,
  setActiveItem,
  activeItem,
  constraint,
  itemWidth,
  positions,
  children,
  setTrackWidth,
}) => {
  const controls = useAnimation();
  const x = useMotionValue(0);
  const node = useRef(null);

  const handleDragEnd = (_, info) => {
    const distance = info.offset.x;
    const velocity = info.velocity.x;
    const direction = velocity < 0 || distance < 0 ? 1 : -1;
    const isMinDistanceForChange = Math.abs(distance) > 75;
    const nextItem = activeItem + 1;
    const prevItem = activeItem - 1;
    const isAtEnd = activeItem >= positions.length - 1;
    const isAtStart = activeItem === 0;
    const isForwards = direction === 1;
    const isBackwards = direction === -1;

    if (!isMinDistanceForChange) return;
    if (isForwards && isAtEnd) return;
    if (isBackwards && isAtStart) return;

    if (isForwards) {
      setActiveItem(nextItem);
      controls.start({
        x: positions[nextItem],
        transition: {
          velocity: info.velocity.x,
          ...transitionProps,
        },
      });
    }

    if (isBackwards) {
      setActiveItem(prevItem);
      controls.start({
        x: positions[prevItem],
        transition: {
          velocity: info.velocity.x,
          ...transitionProps,
        },
      });
    }
  };

  const handleResize = useCallback(() => {
    return controls.start({
      x: positions[activeItem],
      transition: {
        ...transitionProps,
      },
    });
  }, [controls, positions, activeItem]);

  const handleClick = useCallback(
    (event) =>
      node?.current?.contains(event.target) ? setTrackIsActive(true) : setTrackIsActive(false),
    [setTrackIsActive]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (trackIsActive) {
        if (activeItem < positions.length - constraint) {
          if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveItem((prev) => prev + 1);
          }
        }
        if (activeItem > positions.length - positions.length) {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveItem((prev) => prev - 1);
          }
        }
      }
    },
    [trackIsActive, setActiveItem, activeItem, constraint, positions.length]
  );

  useEffect(() => {
    handleResize();
    setTrackWidth(node?.current?.offsetWidth);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [handleClick, handleResize, handleKeyDown, positions, setTrackWidth]);

  return (
    <>
      {itemWidth && (
        <VStack spacing={5} alignItems="stretch" w="90%">
          <MotionFlex
            ref={node}
            id="motion"
            dragConstraints={node}
            onDragEnd={handleDragEnd}
            animate={controls}
            style={{ x }}
            drag="x"
            _active={{ cursor: 'grabbing' }}
            minWidth="min-content"
            flexWrap="nowrap"
            cursor="grab"
          >
            {children}
          </MotionFlex>
        </VStack>
      )}
    </>
  );
};

const Item = ({
  setTrackIsActive,
  setActiveItem,
  activeItem,
  constraint,
  itemWidth,
  positions,
  children,
  index,
  gap,
}) => {
  const [userDidTab, setUserDidTab] = useState(false);

  const handleFocus = () => setTrackIsActive(true);

  const handleBlur = () => {
    userDidTab && index + 1 === positions.length && setTrackIsActive(false);
    setUserDidTab(false);
  };

  const handleKeyUp = (event) =>
    event.key === 'Tab' && !(activeItem === positions.length - constraint) && setActiveItem(index);

  const handleKeyDown = (event) => event.key === 'Tab' && setUserDidTab(true);

  return (
    <MotionFlex
      id={`item-${index}`}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyUp={handleKeyUp}
      onKeyDown={handleKeyDown}
      w={`${itemWidth}px`}
      _notLast={{
        mr: `${gap}px`,
      }}
      py={1}
      animate={{ marginLeft: '0px' }}
    >
      {children}
    </MotionFlex>
  );
};

export const ItemUpsellCarousel = ({ children: allChildren, gap, isLoading }) => {
  const [trackIsActive, setTrackIsActive] = useState(false);
  const [sliderWidth, setSliderWidth] = useState(1);
  const [activeItem, setActiveItem] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [multiplier, setMultiplier] = useState(0.35);
  const [constraint, setConstraint] = useState(1);
  const [trackWidth, setTrackWidth] = useState(0);
  const { sendEvent } = useSendEvent();
  const globalUIDispatch = useGlobalUIDispatch();
  const { hasSentUpsellCarouselShownEvent } = useGlobalUIState();
  const { isXsDown, isSmDown, isMdDown, isMd } = useScreenSizeState();
  const initSliderWidth = useCallback((width) => setSliderWidth(width), []);
  const children = allChildren.slice(0, 3);
  const positions = useMemo(
    () =>
      children.map((_, index) => {
        if (index === 0) {
          return -Math.abs(0);
        } else if (index === children.length - 1) {
          return -Math.abs(trackWidth - sliderWidth);
        } else {
          return -Math.abs(trackWidth / 2 - sliderWidth / 2);
        }
      }),
    [children, sliderWidth, trackWidth]
  );
  const atStart = activeItem === 0;
  const atEnd = activeItem + constraint >= children.length;
  const allItemsFit = atStart && atEnd;

  useMount(() => {
    if (!hasSentUpsellCarouselShownEvent) {
      sendEvent(EAnalyticsEventNames.DEFAULT, {
        googleAnalytics: {
          eventInfo: {
            location: EGALocations.ONLINE_ORDER,
            action: 'view',
            object: EAnalyticsEventNames.UPSELL_CAROUSEL_SHOWN,
          },
        },
      });
      globalUIDispatch({ type: 'SENT_UPSELL_CAROUSEL_SHOWN_EVENT' });
    }
  });

  useEffect(() => {
    setActiveItem(0);
    if (isXsDown || isMd) {
      setItemWidth(positions?.length === 1 ? sliderWidth - 16 : sliderWidth * 0.75);
      setMultiplier(0.75);
      setConstraint(1);
      return;
    }
    if (isSmDown) {
      setItemWidth(sliderWidth * 0.45);
      setMultiplier(0.75);
      setConstraint(1);
      return;
    }
    if (isMdDown) {
      setItemWidth(sliderWidth / 3 - 12);
      setMultiplier(1);
      setConstraint(3);
      return;
    }
  }, [sliderWidth, gap, isXsDown, isMd, isSmDown, isMdDown, positions?.length]);

  const sliderProps = {
    setTrackIsActive,
    initSliderWidth,
    setActiveItem,
    activeItem,
    constraint,
    positions,
    gap,
  };

  const trackProps = {
    setTrackIsActive,
    setTrackWidth,
    trackIsActive,
    setActiveItem,
    sliderWidth,
    activeItem,
    constraint,
    multiplier,
    itemWidth,
    positions,
    gap,
  };

  const itemProps = {
    setTrackIsActive,
    trackIsActive,
    setActiveItem,
    activeItem,
    constraint,
    itemWidth,
    positions,
    gap,
  };

  return (
    <TitleAndIcons
      {...sliderProps}
      atStart={atStart}
      atEnd={atEnd}
      allItemsFit={allItemsFit}
      isLoading={isLoading}
    >
      <Track {...trackProps}>
        {children.map((child, index) => (
          <Item {...itemProps} index={index} key={`recommendation-${index + 1}`}>
            {child}
          </Item>
        ))}
      </Track>
    </TitleAndIcons>
  );
};
